import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProfile, logout } from "../../store/slices/authSlice";
import { WorkingCompanySelect } from "./WorkingCompany";
import { authApi } from "../../api/authApi";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import UserProfileModal from "../ui/UserProfileModal"; // 📦 Imported our new JS profile modal
import mbhcImage from "../../assets/images/mbhc-logo.jpg";
import usePermissions from "../../hooks/usePermissions";
import NotificationBell from "./NotificationBell";
import "./NavBar.css";

/** onMenu: phones open the sidebar menu (both views pass it). */
const Navbar = ({ onMenu }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Local state to manage whether the profile modal is visible
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profileOnPassword, setProfileOnPassword] = useState(false);

  // Destructure the new fields we added to the auth slice
  const { user, role, accessToken, status } = useSelector(
    (state) => state.auth,
  );
  const location = useLocation();
  const { pathname } = location;
  // Old /change-password links (signed in) land here with the profile popup open
  useEffect(() => {
    if (location.state?.openChangePassword) {
      setProfileOnPassword(true);
      setIsProfileOpen(true);
    }
  }, [location.state]);
  // My view ⇄ HR view: only for users who have both (HR access + linked employee)
  const { hasHrAccess, employeeId } = usePermissions();
  const showToggle = hasHrAccess && !!employeeId;
  const inMyView = pathname === "/me" || pathname.startsWith("/me/");

  // Both views show the profile in the top bar: load it once per session
  useEffect(() => {
    if (accessToken && !user && status !== "loading" && status !== "failed")
      dispatch(getProfile());
  }, [accessToken, user, status, dispatch]);

  const queryClient = useQueryClient();

  const handleLogout = async () => {
    // End the session on the server too; log out locally even if that fails
    try {
      await authApi.logout();
    } catch {
      /* token already invalid or offline */
    }
    dispatch(logout());
    // Drop all cached server data so the next user never sees it
    queryClient.clear();
    navigate("/login");
  };

  // Helper to get a clean display name
  const displayName = user?.email?.split("@")[0] || "User";

  return (
    <>
      <nav className="navbar shadow-sm navbar-top">
        <div className="container-fluid d-flex align-items-center flex-nowrap px-0">
          {onMenu && (
            <button
              type="button"
              className="btn btn-sm btn-light border me-2 d-md-none navbar-menu"
              onClick={onMenu}
              aria-label="Open menu"
            >
              <i className="bi bi-list"></i>
            </button>
          )}
          {/* BRANDING: Smart HR */}
          <div
            className="d-flex align-items-center gap-2 cursor-pointer"
            // onClick={() => navigate("/")}
          >
            <div className="navbar-logo">
              <img src={mbhcImage} alt="Logo" />
            </div>
            <span className="fw-bold ls-n05 fs-125 text-navy">
              HR<span className="text-blue-500">IS</span>
            </span>
          </div>

          {/* USER PROFILE & LOGOUT */}
          <div className="ms-auto d-flex align-items-center min-w-0">
            {showToggle && (
              <div
                className="btn-group btn-group-sm me-3 view-toggle"
                role="group"
                aria-label="Switch view"
              >
                <button
                  type="button"
                  className={`btn ${inMyView ? "btn-primary" : "btn-outline-secondary"}`}
                  aria-pressed={inMyView}
                  onClick={() => !inMyView && navigate("/me")}
                >
                  <i className="bi bi-person me-1"></i>
                  <span className="d-none d-sm-inline">My view</span>
                </button>
                <button
                  type="button"
                  className={`btn ${!inMyView ? "btn-primary" : "btn-outline-secondary"}`}
                  aria-pressed={!inMyView}
                  onClick={() => inMyView && navigate("/dashboard")}
                >
                  <i className="bi bi-briefcase me-1"></i>
                  <span className="d-none d-sm-inline">HR view</span>
                </button>
              </div>
            )}
            {/* Phones: the company picker sits under the bar (Layout) */}
            {!inMyView && (
              <div className="d-none d-md-flex">
                <WorkingCompanySelect />
              </div>
            )}
            {accessToken && <NotificationBell />}
            {user && (
              <>
                {/* Clicking either the text details or avatar opens your modal */}
                <div
                  className="d-flex flex-column text-end me-3 d-none d-md-block cursor-pointer"
                  onClick={() => setIsProfileOpen(true)}
                >
                  <span className="fw-bold fs-085 text-slate-800 d-block lh-12">
                    {displayName}
                  </span>
                  <span className="text-muted fs-070 fw-semibold text-uppercase">
                    {role}{" "}
                    {user.branch?.branchName
                      ? `• ${user.branch.branchName}`
                      : ""}
                  </span>
                </div>

                {/* Avatar blocks acting as modal view toggle trigger */}
                <div
                  className="profile-trigger shadow-sm navbar-avatar"
                  onClick={() => setIsProfileOpen(true)}
                >
                  {user.email?.charAt(0).toUpperCase()}
                </div>
              </>
            )}

            <button
              className="btn btn-sm d-flex align-items-center gap-2 navbar-logout"
              onClick={handleLogout}
              aria-label="Logout"
            >
              <i className="bi bi-box-arrow-right"></i>
              <span className="d-none d-sm-inline">Logout</span>
            </button>
          </div>
        </div>
      </nav>

      {/* 🔐 PROFILE DISPLAY CONTAINER */}
      <UserProfileModal
        isOpen={isProfileOpen}
        startOnPassword={profileOnPassword}
        onClose={() => {
          setIsProfileOpen(false);
          setProfileOnPassword(false);
        }}
      />
    </>
  );
};

export default Navbar;
